// React
import { type ReactNode, Suspense, lazy, useCallback, useEffect, useState } from 'react';
// Redux
import { useSelector } from 'react-redux';
// React Router
import { useHistory } from 'react-router-dom';

// UI
import { cn } from '@cobliteam/ui/utils';

// Common Types
import { Location, UserRole, locationName } from '@cobliteam/common-types';

// Analytics
import { Analytics } from '@cobliteam/analytics';
// Cookie Manager
import { CookieManager } from '@cobliteam/cookie-manager';
import { useOnboardingStore } from '@cobliteam/onboarding';
// Preferences
import Preferences from '@cobliteam/preferences';

// Store
import { type AppState, selectLocationPathname } from '@cobliteam/store';

// Hooks
import { useCrm, useUser } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

// Base Components
import { LOW_RESOLUTION_COOKIE_NAME, LowResolutionPlaceholder } from '@cobliteam/base-components';

// Local Components
import NavbarDashboard from '../../components/NavbarDashboard';
import SidebarContainer from '../../components/Sidebar';

import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';
// Third Party Configuration
import { configureAnalytics, configureClarity, configureDatadogRUM, configureOneSignal, configureSentry } from '../../libs/configureThirdParty';
import LoadingPage from '../../pages/LoadingPage';

// Lazy Imports
const DeveloperTools = lazy(() => import('@cobliteam/developer-tools'));

const convertLocationToRegexPattern = (inputString: string): RegExp => {
  const escapedString = inputString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexPattern = escapedString.replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${regexPattern}$`);
};

const getLocationName = (location: string): string => {
  const locationsArray = Object.values(Location);

  for (const loc of locationsArray) {
    if (loc === location) {
      return locationName[loc];
    }

    const regex = convertLocationToRegexPattern(loc);
    if (regex.test(location)) {
      return locationName[loc];
    }
  }

  return 'não encontrada';
};

const AppLayout = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const currentLocation = useSelector((state: AppState) => selectLocationPathname(state));
  const { ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum } = useFeatureFlags();

  // User
  const { getSession, useGetUserData } = useUser();
  const session = getSession();
  const { data: user } = useGetUserData();
  const userAttributes = user?.userAttributes;

  // CRM
  const { useGetDealIdHubspot } = useCrm();
  const { data: deal, isFetching: dealIsFetching } = useGetDealIdHubspot();

  // Onboarding
  const [shouldContinue, setShouldContinue] = useState(CookieManager.getCookie(LOW_RESOLUTION_COOKIE_NAME) === 'true');
  const { progressBar } = useOnboardingStore();

  // Utils
  const isLocalhost = window.location.hostname.includes('localhost');
  const isPreview = window.location.hostname.includes('preview');
  const isDev = isLocalhost || isPreview;

  // Third Party Configuration
  const configureThirdPartyServices = useCallback(() => {
    configureClarity(session);
    configureSentry(session);

    if (session.user.role !== UserRole.MAP_VIEW && oneSignal) {
      configureOneSignal(session);
    }

    if (useDdRum && ddRumSampleRate) {
      const sampleRate = ddRumSampleRate;
      const replaySampleRate = ddRumReplaySampleRate || 0;
      configureDatadogRUM(session, sampleRate, replaySampleRate, userAttributes);
    }
  }, [session, userAttributes, dealIsFetching, oneSignal, useDdRum, ddRumSampleRate, ddRumReplaySampleRate, deal]);

  const continueToDashboard = () => {
    CookieManager.setCookie(LOW_RESOLUTION_COOKIE_NAME, 'true');
    setShouldContinue(true);
  };

  const configAnalytics = useCallback(() => {
    const userAttributes = user?.userAttributes;
    configureAnalytics(session, userAttributes);
  }, [session, user?.userAttributes]);

  useEffect(() => {
    configAnalytics();
  }, [configAnalytics]);

  useEffect(() => {
    Analytics.initialize();
    Preferences.initialize();
  }, []);

  useEffect(() => {
    const urlToRedirect = redirectSessionStorage();
    if (urlToRedirect) history.push(urlToRedirect);
  }, [history, redirectSessionStorage]);

  useEffect(() => {
    configureThirdPartyServices();
  }, [configureThirdPartyServices]);

  useEffect(() => {
    if (session) {
      Analytics.pageview(locationName[currentLocation], {
        fleetId: session.fleet.id,
      });
    }
  }, [currentLocation, session]);

  if (currentLocation === Location.ONBOARDING_SETUP) return null;

  if (!shouldContinue && window.innerWidth <= 720) return <LowResolutionPlaceholder onContinue={continueToDashboard} />;

  return (
    <>
      {isDev && (
        <Suspense fallback={<div>Carregando...</div>}>
          <DeveloperTools />
        </Suspense>
      )}
      <div className={cn('box-border grid h-screen grid-cols-[auto_1fr] overflow-hidden', progressBar && 'pt-[54px]')}>
        <nav>
          <SidebarContainer />
        </nav>
        <main className='overflow-hidden'>
          <NavbarDashboard />
          {/*
            ID=DATA TABLE VIRTUALIZER -
            This id is used to render the table in the virtualizer mode pages: /camera/events | /media/events | /triggers
          */}
          <section id='data-table-virtualizer' className='relative h-screen-without-header w-full overflow-y-auto'>
            <Suspense
              fallback={<LoadingPage dataTestId='loading-page' message={`Carregando página de ${getLocationName(currentLocation)?.toLocaleLowerCase() || 'não encontrada'}...`} />}
            >
              {children}
            </Suspense>
          </section>
        </main>
      </div>
      <div id='page-components' />
    </>
  );
};

export default AppLayout;
